import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import {
  checkAuthState as checkAuth,
  login as loginAPI,
  logout as logoutAPI,
} from "../services/apiService";

const AuthContext = createContext();

// Where to send the user after they sign in. SAML login is a full-page
// round trip through the backend (which lands back on /login), so the
// in-memory router state is lost — the path is stashed in sessionStorage
// before redirecting and read back once check-auth succeeds.
const LOGIN_REDIRECT_KEY = "tracer.loginRedirect";

export const stashLoginRedirect = (path) => {
  try {
    if (path) sessionStorage.setItem(LOGIN_REDIRECT_KEY, path);
  } catch (e) {
    // Storage unavailable (private mode etc.) — fall back to the default page.
  }
};

// Returns the stashed path (if any) and clears it so it's only used once.
export const consumeLoginRedirect = () => {
  try {
    const path = sessionStorage.getItem(LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(LOGIN_REDIRECT_KEY);
    return path;
  } catch (e) {
    return null;
  }
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children, onLogin, onLogout }) => {
  const [currentUser, setCurrentUser] = useState(null);
  // False until the initial check-auth resolves, so guards can wait instead
  // of treating "not checked yet" as "logged out".
  const [authChecked, setAuthChecked] = useState(false);

  const checkAuthState = useCallback(() => {
    checkAuth()
      .then((data) => {
        if (data.authenticated) {
          setCurrentUser(data.user);
        } else {
          setCurrentUser(null);
        }
      })
      .catch((error) => {
        console.error("Error checking auth state:", error);
      })
      .finally(() => {
        setAuthChecked(true);
      });
  }, []);

  const handleLogin = useCallback(() => {
    loginAPI().catch((error) => {
      console.error("Error during login:", error);
    });
  }, []);

  const logout = useCallback(() => {
    logoutAPI()
      .then(() => {
        setCurrentUser(null);
        onLogout && onLogout();
      })
      .catch((error) => {
        console.error("Error logging out:", error);
      });
  }, [onLogout]);

  useEffect(() => {
    checkAuthState();
  }, [checkAuthState]);

  const value = {
    currentUser,
    authChecked,
    login: handleLogin,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
